import { useEffect, useMemo, useState } from "react";
import { Coins } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import { api } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import {
  normalizeModel,
  calculateFeed,
  computeCost,
  PRICE_ITEMS,
  EMPTY_PRICES,
  fmt,
  fmtRupiah,
} from "@/lib/feedModels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const latestWeight = (c) => (c.weights?.length ? c.weights[c.weights.length - 1].weight : 0);

export default function HppPage() {
  const { config } = useConfig();
  const zones = config.zones || [];
  const models = (config.feed_models || []).map(normalizeModel);
  const overheadComponents = config.overhead_components || [];

  const [cattle, setCattle] = useState([]);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [selected, setSelected] = useState({});
  const [modelId, setModelId] = useState("");
  const [prices, setPrices] = useState(EMPTY_PRICES);
  const [feedOverride, setFeedOverride] = useState("");
  const [overheadVals, setOverheadVals] = useState({});

  useEffect(() => {
    api.get("/cattle").then((r) => setCattle(r.data)).catch(() => toast.error("Gagal memuat data sapi"));
  }, []);
  useEffect(() => {
    if (!modelId && models.length) setModelId(models[0].id);
  }, [models, modelId]);

  const anySel = Object.values(selected).some(Boolean);
  const group = anySel
    ? cattle.filter((c) => selected[c.id])
    : zoneFilter === "all"
    ? cattle
    : cattle.filter((c) => (c.zone || "") === zoneFilter);

  const totalWeight = group.reduce((s, c) => s + latestWeight(c), 0);
  const model = models.find((m) => m.id === modelId) || null;

  const autoMonthlyFeed = useMemo(() => {
    const result = calculateFeed(totalWeight, model);
    const cost = computeCost(result, prices);
    return cost ? Math.round(cost.monthlyCost) : 0;
  }, [totalWeight, model, prices]);

  const monthlyFeed = feedOverride !== "" ? parseFloat(feedOverride) || 0 : autoMonthlyFeed;
  const feedPerKg = totalWeight > 0 ? monthlyFeed / totalWeight : 0;
  const totalOverhead = overheadComponents.reduce((s, name) => s + (parseFloat(overheadVals[name]) || 0), 0);
  const overheadPerCattle = group.length > 0 ? totalOverhead / group.length : 0;

  const rows = group.map((c) => {
    const w = latestWeight(c);
    const feedCost = feedPerKg * w;
    const hpp = (c.buy_price || 0) + feedCost + overheadPerCattle;
    return { c, w, feedCost, hpp };
  });

  return (
    <PageContainer title="Hitung Harga Sapi (HPP)" subtitle="Hitung Harga Pokok Produksi tiap sapi dari pakan, overhead, dan harga beli">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6 space-y-4">
            <h2 className="font-display text-lg font-bold text-[#8A5230]">1. Pilih Sapi</h2>
            <div>
              <Label className="text-sm font-semibold text-[#7A4A28]">Berdasarkan Zona</Label>
              <Select value={zoneFilter} onValueChange={(v) => { setZoneFilter(v); setSelected({}); }}>
                <SelectTrigger data-testid="hpp-zone-filter" className="bg-[#FBF3E8] border-[#E0A988]/60 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Zona</SelectItem>
                  {zones.map((z) => (<SelectItem key={z} value={z}>{z}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-[#A06E4D]">Atau centang sapi tertentu di bawah (mengabaikan filter zona).</p>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-[#F0E3D2] divide-y divide-[#F0E3D2]">
              {cattle.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#FBF3E8]">
                  <input type="checkbox" data-testid={`hpp-check-${c.id}`} checked={!!selected[c.id]}
                    onChange={(e) => setSelected((p) => ({ ...p, [c.id]: e.target.checked }))}
                    className="w-4 h-4 accent-[#A06E4D]" />
                  <span className="text-[#7A4A28]">{c.name}</span>
                  <span className="text-[#C99A6A] text-xs ml-auto">{fmt(latestWeight(c))} kg{c.zone ? ` · ${c.zone}` : ""}</span>
                </label>
              ))}
            </div>
            <div className="rounded-lg bg-[#8A5230] text-white px-4 py-2 flex justify-between">
              <span className="text-sm">{group.length} sapi · Total bobot</span>
              <span data-testid="hpp-total-weight" className="font-mono-num font-bold">{fmt(totalWeight)} kg</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6 space-y-4">
            <h2 className="font-display text-lg font-bold text-[#8A5230]">2. Biaya Pakan Sebulan</h2>
            <div>
              <Label className="text-sm font-semibold text-[#7A4A28]">Model Pakan</Label>
              <Select value={modelId} onValueChange={setModelId}>
                <SelectTrigger data-testid="hpp-model" className="bg-[#FBF3E8] border-[#E0A988]/60 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m) => (<SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PRICE_ITEMS.map((it) => (
                <div key={it.id}>
                  <Label className="text-[11px] text-[#7A4A28]">{it.label} (Rp/kg)</Label>
                  <Input data-testid={`hpp-price-${it.id}`} type="number" min="0" value={prices[it.id]}
                    onChange={(e) => setPrices({ ...prices, [it.id]: e.target.value })}
                    className="bg-[#FBF3E8] border-[#E0A988]/60 h-9 font-mono-num" />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#7A4A28]">Biaya Pakan Sebulan (grup) — bisa diedit</Label>
              <Input data-testid="hpp-feed-monthly" type="number" min="0"
                placeholder={String(autoMonthlyFeed)}
                value={feedOverride}
                onChange={(e) => setFeedOverride(e.target.value)}
                className="bg-[#FBF3E8] border-[#E0A988]/60 mt-1 font-mono-num" />
              <p className="text-xs text-[#A06E4D] mt-1">Otomatis: {fmtRupiah(autoMonthlyFeed)} (kosongkan untuk pakai nilai otomatis)</p>
            </div>
            <div className="rounded-lg bg-[#FBEAD6] border border-[#E0A988]/50 px-4 py-2 flex justify-between">
              <span className="text-sm font-semibold text-[#8A5230]">Biaya Pakan per kg/bulan</span>
              <span data-testid="hpp-feed-perkg" className="font-mono-num font-bold text-[#7A4A28]">{fmtRupiah(feedPerKg)}</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6 space-y-3">
            <h2 className="font-display text-lg font-bold text-[#8A5230]">3. Biaya Overhead</h2>
            {overheadComponents.length === 0 && (
              <p className="text-sm text-[#C99A6A]">Belum ada komponen overhead. Tambahkan di menu Pengaturan.</p>
            )}
            {overheadComponents.map((name) => (
              <div key={name} className="flex items-center gap-3">
                <span className="w-28 text-sm text-[#7A4A28]">{name}</span>
                <Input data-testid={`hpp-overhead-${name}`} type="number" min="0" placeholder="0"
                  value={overheadVals[name] ?? ""}
                  onChange={(e) => setOverheadVals({ ...overheadVals, [name]: e.target.value })}
                  className="bg-[#FBF3E8] border-[#E0A988]/60 h-9 font-mono-num" />
              </div>
            ))}
            <div className="rounded-lg bg-[#FBEAD6] border border-[#E0A988]/50 px-4 py-2 flex justify-between">
              <span className="text-sm font-semibold text-[#8A5230]">Total Overhead / {group.length || 0} sapi</span>
              <span data-testid="hpp-overhead-percattle" className="font-mono-num font-bold text-[#7A4A28]">{fmtRupiah(overheadPerCattle)}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#F7E5D2] flex items-center justify-center">
                <Coins className="w-4 h-4 text-[#A06E4D]" />
              </div>
              <h2 className="font-display text-lg font-bold text-[#8A5230]">HPP per Sapi</h2>
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-[#C99A6A] py-6 text-center">Pilih sapi terlebih dahulu.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#F0E3D2]">
                <Table data-testid="hpp-table">
                  <TableHeader>
                    <TableRow className="bg-[#FBF3E8] [&>th]:text-center">
                      <TableHead className="text-xs font-semibold text-center">Nama</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Nomor Sapi</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Bobot</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Harga Beli</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Biaya Pakan</TableHead>
                      <TableHead className="text-xs font-semibold text-center">Overhead</TableHead>
                      <TableHead className="text-xs font-semibold text-center">HPP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(({ c, w, feedCost, hpp }) => (
                      <TableRow key={c.id} data-testid={`hpp-row-${c.id}`} className="[&>td]:text-center">
                        <TableCell className="text-sm font-medium text-[#7A4A28]">{c.name}</TableCell>
                        <TableCell data-testid={`hpp-cattle-number-${c.id}`} className="font-mono-num text-sm">{c.cattle_number || "-"}</TableCell>
                        <TableCell className="font-mono-num text-sm">{fmt(w)} kg</TableCell>
                        <TableCell className="font-mono-num text-sm">{fmtRupiah(c.buy_price || 0)}</TableCell>
                        <TableCell className="font-mono-num text-sm">{fmtRupiah(feedCost)}</TableCell>
                        <TableCell className="font-mono-num text-sm">{fmtRupiah(overheadPerCattle)}</TableCell>
                        <TableCell data-testid={`hpp-value-${c.id}`} className="font-mono-num text-sm font-bold text-[#8A5230]">{fmtRupiah(hpp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
